import React, { useState, useEffect, useCallback } from "react";
import { useLocation, } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import { commaSepThou, getCookie } from '../../util.js';
import { Dialog, Button } from 'evergreen-ui';
import { gql } from '@apollo/client';

// ------- QUERIES
import QUERY_CREATORS from "../../graphql/queries/Creators/QueryCreators.js";

// ------- COMPONENTS
import LandingPage from "../../components/layout/LandingPage.js";
import NewCreator from "./NewCreator.js";
import CsvUpload from "../../components/layout/CsvUpload.js";
import { CreatorImportModal } from "../../components/csv-import/modals";

import {
  Pane,
  AddIcon,
  SideSheet,
  DocumentShareIcon,
  ImportIcon
} from 'evergreen-ui';
import CloudImage from "../../components/CloudImage.js";

// Add this mutation
const CREATE_PAYMENT_TYPE = gql`
  mutation CreatePaymentType($publisherId: ID!, $value: String!) {
    createPaymentType(publisherId: $publisherId, value: $value) {
      paymentType {
        id
        value
      }
    }
  }
`;

function Creators() {

  // ------- STATES
  const location = useLocation();
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const [creators, setCreators] = useState([]);
  const [count, setCount] = useState(0);
  const [pagecount, setPagecount] = useState(0);
  const [currpage, setCurrpage] = useState(1);
  const [newmode, setNewmode] = useState(false);
  const [csvmode, setCsvmode] = useState(false);
  const [advancedImportMode, setAdvancedImportMode] = useState(false);
  const [params, setParams] = useState({});
  const [sourcePageInfo, setSourcePageInfo] = useState('');
  const [showPaymentTypeDialog, setShowPaymentTypeDialog] = useState(false);
  const [unknownPaymentTypes, setUnknownPaymentTypes] = useState([]);
  const [currentPaymentType, setCurrentPaymentType] = useState(null);

  // ------- HOOKS

  const { loading, error, data, refetch: _refetch } = useQuery(QUERY_CREATORS, {
    variables: {
      publisher: String(currentpublisher.id),
      page: parseInt(currpage),
      params: JSON.stringify(params)
    },
  });

  const refetch = useCallback(() => { setTimeout(() => _refetch(), 0) }, [_refetch]);

  const [createPaymentType] = useMutation(CREATE_PAYMENT_TYPE);

  useEffect(() => {
    if (data) {
      setCreators(data.creators);
      const csrftoken = getCookie('csrftoken');
      fetch('/count_creators/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify({ publisher: currentpublisher.id })
      })
        .then(res => res.json())
        .then(data => {
          setCount(parseInt(data.message));
          setPagecount(Math.ceil(data.message / 25));
        })
    }
  }, [currentpublisher.id, data]);

  useEffect(() => {
    if (location.state) {
      let locState = location.state;
      setSourcePageInfo(locState)
    }
  }, [location.state]);

  // ------- FUNCTIONS

  const handleRefetchCreators = () => {
    setParams({});
    refetch();
  }

  const handleToggleNewMode = () => {
    setNewmode(!newmode);
  }

  const handleToggleCsvMode = () => {
    setCsvmode(!csvmode);
    refetch();
  }

  const handleToggleAdvancedImportMode = () => {
    setAdvancedImportMode(!advancedImportMode);
  }

  const handleAddPaymentType = async (paymentType) => {
    try {
      const { data } = await createPaymentType({
        variables: {
          value: paymentType,
          publisherId: currentpublisher.id.toString()
        }
      });

      if (data.createPaymentType.paymentType) {
        setUnknownPaymentTypes(prev => prev.filter(pt => pt !== paymentType));
        if (unknownPaymentTypes.length <= 1) {
          setShowPaymentTypeDialog(false);
        } else {
          setCurrentPaymentType(unknownPaymentTypes[1]);
        }
      }
    } catch (error) {
      console.error('Error adding payment type:', error);
    }
  };

  // ------- INPUTS
  const buttons = [
    { title: "NEW CREATOR", icon: AddIcon, onClick: setNewmode },
    // { title: "IMPORT CREATORS", icon: DocumentShareIcon, onClick: setCsvmode },
    { title: "IMPORT CREATORS", icon: ImportIcon, onClick: handleToggleAdvancedImportMode },
  ];

  const cols = [
    { title: "DISPLAY NAME", type: "button", flex: "2", query: "display_name" },
    { title: "PAYMENT NAME", type: "button", flex: "2", query: "payment_name" },
    { title: "EMAIL", type: "default", flex: "2" },
    { title: "PHONE", type: "default", flex: "2" },
    { title: "BALANCE", type: "default", flex: "1", align:"right" },
  ]

  const searchInit = {
    all: "",
    id: "",
    payment_name: "",
    display_name: ""
  }

  const searchInputs = {
    default: { searchq: 'all', dropLabel: 'Default' },
    id: { searchq: 'id', dropLabel: 'By ID' },
    display_name: { searchq: 'display_name', dropLabel: 'By Display Name' },
    payment_name: { searchq: 'payment_name', dropLabel: 'By Payment Name' },
  }

  const filterInputs = {
  }

  const csvFields = [
    "biography",
    "created",
    "displayName",
    "id",
    "paymentName",
    "residence",
    "creatoremailSet",
    "creatorphonenumberSet",
    "creatoraddressSet",
    "paymentType",
    "website",
  ];

  const csvConfig = {
    "paymentType": ["value"],
    "sets": {},
    "primarySets": {
      "creatoremailSet": {
        "fields": ["value"],
        "subfields": {},
        "label": "emails"
      },
      "creatorphonenumberSet": {
        "fields": ["value"],
        "subfields": {},
        "label": "phoneNumbers"
      },
      "creatoraddressSet": {
        "fields": ["line1", "line2", "city", "stateOrRegion", "country", "zipcode"],
        "subfields": {},
        "label": "addresses"
      }

    },
    "arrays": {}
  }

  const csvTemplate = [
    [
      "display_name",
      "payment_name",
      "residence",
      "payment_type",
      "biography",
      "website",
      "email",
      "phone",
      "address_line1",
      "address_line2",
      "address_city",
      "address_region",
      "address_zipcode",
      "address_country"
    ],
    [
      "Creator's display name in Working Lit - must be unique!",
      "Creator's payment name (e.g. for sending mail)",
      "",
      "The preferred payment type as it exists in Working Lit",
      "Author bio (CANNNOT HAVE COMMAS)",
      "Website",
      "Primary email",
      "Primary phone number",
      "Street address",
      "Street address - line 2",
      "City",
      "State or Region",
      "Zipcode",
      "Country"

    ],
    [
      "bell hooks",
      "Gloria Jean Watkins",
      "Kentucky",
      "Check",
      "bell hooks was an American author and theorist who worked as a Distinguished Professor in Residence at Berea College. She is best known for her writings on race, feminism, and class.",
      "https://example.com",
      "bellhooks@imaginarydomain.org",
      "1-234-567-8989",
      "37 Book Rd",
      "",
      "Berea",
      "KY",
      "40403",
      "USA"
    ]
  ];

  const csvWidths = [
    3,
    3,
    2,
    2,
    4,
    3,
    2,
    3,
    2,
    2,
    2,
    2,
    3
  ]

  if (error) return `Error! ${error}`;

  return (
    <Pane position="absolute" padding={16} width="100%" height="100%">
      {csvmode ? (
        <>
          <CsvUpload
            handleToggleCsvMode={handleToggleCsvMode}
            template={csvTemplate}
            itemType="creator"
            tableWidth={2000}
            colWidths={csvWidths}
            onValidationError={(errors) => {
              if (errors.unknown_payment_types) {
                setUnknownPaymentTypes(errors.unknown_payment_types);
                setCurrentPaymentType(errors.unknown_payment_types[0]);
                setShowPaymentTypeDialog(true);
              }
            }}
          />

          <Dialog
            isShown={showPaymentTypeDialog}
            title="Unknown Payment Type"
            onCloseComplete={() => setShowPaymentTypeDialog(false)}
            confirmLabel="Yes, add it"
            cancelLabel="Cancel, I will fix the spreadsheet"
            onConfirm={() => handleAddPaymentType(currentPaymentType)}
            intent="none"
            confirmAppearance="dialogConfirm"
            cancelAppearance="dialogCancel"
          >
            This payment type "{currentPaymentType}" was not found in your preferences. Would you like us to add it?
          </Dialog>
        </>
      ) : (
        <Pane>
          <SideSheet isShown={newmode} onCloseComplete={() => setNewmode(false)} className="background">
            <NewCreator
              handleToggleNewMode={handleToggleNewMode}
              handleRefetchCreators={handleRefetchCreators}
              publisher={currentpublisher.id}
            />
          </SideSheet>

          <LandingPage
            title="Creators"
            location={location}
            sourcePageInfo={sourcePageInfo}
            data={creators}
            loading={loading}
            buttons={buttons}
            count={count}
            pagecount={pagecount}
            searchInputs={searchInputs}
            filterInputs={filterInputs}
            searchInit={searchInit}
            csvConfig={csvConfig}
            csvFields={csvFields}
            csvType="Creators"
            cols={cols}
            refetch={refetch}
            setParams={setParams}
            RowComponent={CreatorRow}
            currpage={currpage}
            setCurrpage={setCurrpage}
            colWidths={["0.01"].concat(cols.map((col) => col.flex))}
          />
        </Pane>)}

      <CreatorImportModal isShown={advancedImportMode} onClose={handleToggleAdvancedImportMode} />

    </Pane>
  )
}

function CreatorRow({ item, allmode, handleCheck, colWidths, makeIDCell, makeTableCellArray }) {
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  let balance = 0;
  item.royaltySet.forEach(royalty => {
    const owed = parseFloat(royalty.owed) || 0;
    const paid = parseFloat(royalty.paid) || 0;
    balance += owed - paid;
  });
  if (isNaN(balance)) balance = 0;
  const balNeg = balance < 0;
  const balAbs = commaSepThou(Math.abs(balance).toFixed(2));

  const [checked, setChecked] = useState(false);

  const handleSetChecked = () => {
    handleCheck(checked, item)
    setChecked(!checked);
  }

  const email = item.creatoremailSet.filter(item => item.primary === true);
  const backup_email = item.creatoremailSet.length > 0
    ? item.creatoremailSet[0].value
    : "No email available";

  const phone = item.creatorphonenumberSet.filter(item => item.primary === true);
  const backup_phone = item.creatorphonenumberSet.length > 0
    ? item.creatorphonenumberSet[0].value
    : "No phone number available";

  const currency = publisherGlobalData.currency
    ? publisherGlobalData.currency.symbol
    : "$";

  const rowCells = [
    {
      content: makeIDCell(checked, handleSetChecked, item.id), disableSelect: true
    },

    {
      content: <Pane display="flex" alignItems="center">
        <CloudImage src={item.photoUrl ? item.photoUrl : ""} avatar="true" size={36} marginY={8} name={item.displayName} />
        <Pane marginLeft={8}>{item.displayName}</Pane></Pane>
    },
    { content: item.paymentName },
    { content: email.length > 0 ? email[0].value : backup_email, disableSelect: true },
    { content: phone.length > 0 ? phone[0].value : backup_phone, disableSelect: true },
    { isNumber: true, content: <Pane>{balNeg && "-"}{currency}{balAbs}</Pane> },
  ]

  useEffect(() => {
    setChecked(allmode);
  }, [allmode]);
  const cellArray = makeTableCellArray(rowCells, colWidths, item.id)

  return cellArray
}

export default Creators;
